/**
 * US A1 (search existing household) + A2 (quick-create in flow) + C2 (household detail /
 * submission timeline). Per ADR-6 (farmer-offline-sync skill §5): creating a household
 * always requires network — the create form is disabled with an explanation when offline,
 * never silently queued.
 */
import * as api from '../api-client.js';
import { formLabel, describeBoundary } from '@farmer/audit-rules';
import { toast, navigate, buildHash, parseHash, debounce, el, esc, formatDate, formatDateTime, statusBadge } from '../ui/util.js';
import { createRegionPicker } from '../ui/region-picker.js';
import { formatMetricValue, metricLabel, sortMetrics } from '../ui/metrics.js';
import { openFormDialog } from '../ui/form-dialog.js';
import { EDIT_REASON_FIELD_HTML, editReasonError, openDeleteDialog } from '../ui/edit-reason.js';
import { renderChangeLog } from '../ui/change-log.js';
import { mountPlotsMap } from '../ui/plot-map.js';
import { openProposalSheet, loadOpenProposals, renderOpenProposals } from '../ui/proposal-sheet.js';
import { pendingByField, displayValue } from '../ui/proposal-fields.js';
import { openHouseholdSubmissionDialog } from './submission-list.js';
import { gpsFieldHtml, attachGpsCapture, readGpsPair } from '../ui/gps-capture.js';
import {
  soilTypeSelectHtml,
  attachSoilTypeHint,
  isSoilType,
  SOIL_TYPE_REQUIRED_MESSAGE,
} from '../ui/soil-type.js';

export async function renderHouseholdSearch(container) {
  const { params } = parseHash();
  const pickMode = params.pick === '1'; // opened from a flow that wants a household chosen
  const returnTo = params.returnTo;

  container.innerHTML = `
    <div class="page-head">
      <div><h1>Hộ &amp; Vườn</h1><p>Tìm hộ theo tên/SĐT/CCCD/mã nông dân (US A1). Bấm vào hộ để kiểm tra thông tin và tạo phiếu.</p></div>
      <button class="btn" id="toggleCreate"><span class="ms">add</span>Tạo hộ mới</button>
    </div>
    <div class="card" id="createCard" hidden></div>
    <div class="filters-bar">
      <input type="search" id="searchInput" class="search-field" placeholder="Tìm theo tên / SĐT / CCCD / mã nông dân...">
    </div>
    <div class="card" id="resultsCard">
      <table class="list-table">
        <thead><tr><th>Họ tên</th><th>Mã nông dân</th><th>SĐT</th><th>Địa bàn</th><th>Số vườn</th><th>Điều tra gần nhất</th><th></th></tr></thead>
        <tbody id="resultsBody"></tbody>
      </table>
      <div id="resultsEmpty" class="empty-state" hidden><span class="ms ms-xl">search_off</span><p>Không tìm thấy hộ nào.</p></div>
      <div id="resultsPaging" class="paging"></div>
    </div>`;

  if (!navigator.onLine) {
    container.querySelector('#toggleCreate').disabled = true;
    container.querySelector('#toggleCreate').title = 'Cần có mạng để tạo hồ sơ mới.';
  }

  container.querySelector('#toggleCreate').addEventListener('click', () => {
    const card = container.querySelector('#createCard');
    card.hidden = !card.hidden;
    // `renderCreateForm` là async từ 2026-08-22 (nó nạp danh sách đơn vị cho admin).
    // Không await ở đây là đúng: người dùng vừa bấm mở thẻ, chờ một request nữa mới
    // hiện gì cả sẽ thành một khoảng trắng không giải thích được. Lỗi nạp đã được
    // xử lý bên trong.
    if (!card.hidden) {
      void renderCreateForm(card, {
        onCreated: (hh) => {
          card.hidden = true;
          search(1);
          if (pickMode) selectHousehold(hh);
        },
      });
    }
  });

  let page = 1;
  const searchInput = container.querySelector('#searchInput');
  const doSearch = debounce(() => search(1), 350);
  searchInput.addEventListener('input', doSearch);

  function selectHousehold(hh) {
    if (returnTo) navigate(buildHash(returnTo, { householdId: hh.id }));
    else navigate(buildHash(`/households/${hh.id}`));
  }

  async function search(p) {
    page = p;
    const body = container.querySelector('#resultsBody');
    const empty = container.querySelector('#resultsEmpty');
    body.innerHTML = `<tr><td colspan="7">Đang tải...</td></tr>`;
    empty.hidden = true;
    try {
      const res = await api.households.list({ search: searchInput.value.trim(), page, pageSize: 20 });
      body.innerHTML = '';
      if (!res.data.length) {
        empty.hidden = false;
      }
      res.data.forEach((hh) => {
        const tr = el('tr');
        const last = hh.lastSubmission
          ? `${formLabel(hh.lastSubmission.formKey, 'short')} · ${formatDate(hh.lastSubmission.submittedAt)}`
          : '—';
        tr.innerHTML = `
          <td><a href="${buildHash(`/households/${hh.id}`)}">${esc(hh.fullName)}</a></td>
          <td class="mono">${esc(hh.farmerCode || '—')}</td>
          <td>${esc(hh.phone || '—')}</td>
          <td>${esc(hh.regionName || '—')}</td>
          <td>${hh.plotsCount}</td>
          <td>${esc(last)}</td>
          <td class="cell-actions">
            ${pickMode ? `<button class="btn btn-tonal sm" data-act="pick">Chọn</button>` : `
            <span class="ms row-chevron" aria-hidden="true">chevron_right</span>`}
          </td>`;
        tr.querySelector('[data-act="pick"]')?.addEventListener('click', () => selectHousehold(hh));
        // Tạo phiếu và Thêm vườn không có ở đây nữa (2026-11-01): bấm vào dòng mở hồ sơ hộ,
        // điều tra viên kiểm thông tin rồi làm ngay trong hồ sơ. Tên hộ vẫn là liên kết cho bàn phím.
        if (!pickMode) {
          tr.classList.add('row-link');
          tr.addEventListener('click', (e) => {
            if (e.target.closest('a, button')) return;
            navigate(buildHash(`/households/${hh.id}`));
          });
        }
        body.appendChild(tr);
      });
      renderPaging(res.meta);
    } catch (err) {
      body.innerHTML = `<tr><td colspan="7">Lỗi: ${esc(err.message)}</td></tr>`;
    }
  }

  function renderPaging(meta) {
    const host = container.querySelector('#resultsPaging');
    host.innerHTML = '';
    if (meta.totalPages <= 1) return;
    const prev = el('button', 'btn btn-text sm', 'Trước');
    prev.disabled = meta.page <= 1;
    prev.addEventListener('click', () => search(meta.page - 1));
    const info = el('span', 'paging-info', `Trang ${meta.page}/${meta.totalPages} (${meta.total} hộ)`);
    const next = el('button', 'btn btn-text sm', 'Sau');
    next.disabled = meta.page >= meta.totalPages;
    next.addEventListener('click', () => search(meta.page + 1));
    host.append(prev, info, next);
  }

  search(1);
}

/**
 * Ô chọn đơn vị cho luồng TẠO hộ — chỉ admin hệ thống cần.
 *
 * Admin hệ thống có `instanceId = null` nên không có gì để suy ra đơn vị đích; máy chủ
 * trả 422 `instanceId` nếu thiếu. Ba vai trò còn lại không thấy ô này: server lấy đơn
 * vị của chính họ, và gửi đơn vị khác vào là 422.
 */
async function loadInstancesForAdmin() {
  if (api.getCurrentUser()?.role !== 'admin') return null;
  try {
    return (await api.instances.list()).filter((d) => d.isActive);
  } catch (err) {
    console.warn('[household] không tải được danh sách đơn vị', err);
    return [];
  }
}

async function renderCreateForm(host, { onCreated }) {
  const donVis = await loadInstancesForAdmin();
  host.innerHTML = `
    <h2 class="card-title"><span class="ms">person_add</span>Tạo hộ mới (US A2)</h2>
    <form id="createHouseholdForm">
      <div class="form-grid">
        ${
          donVis
            ? `<div class="form-row"><label>Đơn vị điều tra *</label>
                 <select name="instanceId" required>
                   <option value="">— Chọn đơn vị —</option>
                   ${donVis.map((d) => `<option value="${esc(d.id)}">${esc(d.name)}</option>`).join('')}
                 </select>
                 <p class="hint-text">Hộ này chỉ hiện với người của đơn vị được chọn.</p></div>`
            : ''
        }
        <div class="form-row"><label>Họ tên chủ hộ *</label><input name="fullName" required></div>
        <div class="form-row"><label>Số điện thoại</label><input name="phone"></div>
        <div class="form-row"><label>CCCD</label><input name="idCardNumber"></div>
        <div class="form-row"><label>Mã nông dân (IDH)</label><input name="farmerCode"></div>
        <div id="regionPickerHost" class="contents"></div>
        <div class="form-row"><label>Địa chỉ chi tiết</label><input name="addressDetail"></div>
        ${gpsFieldHtml()}
      </div>
      <div class="error-text" id="createHouseholdError" hidden></div>
      <div class="form-actions">
        <button class="btn" type="submit">Lưu hộ mới</button>
      </div>
    </form>`;
  const form = host.querySelector('#createHouseholdForm');
  const errBox = host.querySelector('#createHouseholdError');
  const regionPicker = createRegionPicker(host.querySelector('#regionPickerHost'));
  attachGpsCapture(form);
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    errBox.hidden = true;
    const fd = new FormData(form);
    const gps = readGpsPair(fd.get('gpsLat'), fd.get('gpsLng'));
    if (gps.error) {
      errBox.textContent = gps.error;
      errBox.hidden = false;
      return;
    }
    const payload = {
      ...(donVis ? { instanceId: fd.get('instanceId') || undefined } : {}),
      fullName: fd.get('fullName').trim(),
      phone: fd.get('phone').trim() || undefined,
      idCardNumber: fd.get('idCardNumber').trim() || undefined,
      farmerCode: fd.get('farmerCode').trim() || undefined,
      regionId: regionPicker.getValue(),
      addressDetail: fd.get('addressDetail').trim() || undefined,
      gpsLat: gps.gpsLat,
      gpsLng: gps.gpsLng,
    };
    try {
      const hh = await api.households.create(payload);
      toast('Đã tạo hộ mới');
      onCreated(hh);
    } catch (err) {
      // Trùng CCCD là đúng tình huống US A1 sinh ra để tránh: hộ cần nhập đã có
      // sẵn trong danh mục. Đưa thẳng lối sang hồ sơ đó, đừng bắt người dùng
      // thoát form rồi tự đi tìm lại bằng tay.
      if (err.code === 'CONFLICT' && err.existing?.id) {
        errBox.innerHTML = `${esc(err.message)} — <a href="${buildHash(`/households/${err.existing.id}`)}">mở hồ sơ hộ này</a>`;
      } else {
        errBox.textContent = err.message;
      }
      errBox.hidden = false;
    }
  });
}

/** "Xã Ea Tân, Tỉnh Đắk Lắk" — chỉ 2 cấp, không có huyện trong dữ liệu. */
function formatRegion(region) {
  if (!region) return '—';
  return region.parent ? `${region.name}, ${region.parent.name}` : region.name;
}

export async function renderHouseholdDetail(container, params) {
  const { id } = params;
  container.innerHTML = `<div class="empty-state">Đang tải...</div>`;
  let hh;
  try {
    hh = await api.households.get(id);
  } catch (err) {
    container.innerHTML = `<div class="empty-state">${esc(err.message)}</div>`;
    return;
  }

  // US A3 — sửa danh mục là quyền của điều phối viên trở lên (`@Roles` ở API cũng
  // vậy). Điều tra viên vẫn xem được hồ sơ và nhật ký, chỉ không sửa.
  const me = api.getCurrentUser();
  const canManage = ['supervisor', 'instance_admin', 'admin'].includes(me?.role);

  container.innerHTML = `
    <div class="page-head">
      <div><h1>${esc(hh.fullName)}</h1><p>${hh.farmerCode ? 'Mã ' + esc(hh.farmerCode) + ' · ' : ''}${esc(hh.phone || '')}${hh.idCardNumber ? ' · CCCD ' + esc(hh.idCardNumber) : ''}</p></div>
      <div class="page-head-actions">
        <button class="btn btn-text" id="btnReportHousehold"><span class="ms">flag</span>Báo sai thông tin</button>
        ${canManage ? `<button class="btn btn-text" id="btnEditHousehold"><span class="ms">edit</span>Sửa hồ sơ</button>` : ''}
        <button class="btn btn-tonal" id="btnAddPlot"><span class="ms">add_location</span>Thêm vườn</button>
        <button class="btn" id="btnNewSubmission"><span class="ms">note_add</span>Tạo phiếu</button>
      </div>
    </div>
    <div class="two-col">
      <div>
        <div class="card proposals-card" id="proposalsCard" hidden></div>
        <div class="card">
          <h2 class="card-title"><span class="ms">forest</span>Vườn (${hh.plots.length})</h2>
          ${hh.plots.length ? `<table class="list-table"><thead><tr><th>Mã vườn</th><th>Ranh giới</th><th>Diện tích (ha)</th><th>Thành phần đất</th><th>Toạ độ</th><th></th></tr></thead>
            <tbody>${hh.plots.map((p) => `<tr>
                <td>${breakableCode(p.plotCode)}</td>
                <td>
                  ${
                    p.boundaryGeojson
                      ? `<button type="button" class="link-btn boundary-status" data-act="focus-plot" data-id="${esc(p.id)}" title="Xem trên bản đồ">${esc(describeBoundary(p.boundaryGeojson))}</button>`
                      : `<span class="boundary-status">Chưa vẽ</span>`
                  }
                  <a class="btn btn-tonal sm boundary-link" href="${buildHash(`/plots/${p.id}/boundary`)}" title="${p.boundaryGeojson ? 'Sửa ranh giới trên bản đồ' : 'Vẽ ranh giới trên bản đồ'}"><span class="ms ms-sm">map</span>${p.boundaryGeojson ? 'Sửa' : 'Vẽ'}</a>
                </td>
                <td>${esc(p.areaHa)}<span class="pending-slot" data-entity-id="${esc(p.id)}" data-field="areaHa"></span></td>
                <td>${esc(p.soilType || '—')}<span class="pending-slot" data-entity-id="${esc(p.id)}" data-field="soilType"></span></td>
                <td class="mono">${p.gpsLat ?? '—'}, ${p.gpsLng ?? '—'}</td>
                <td class="cell-actions">
                  <button class="icon-btn sm" data-act="report-plot" data-id="${esc(p.id)}" title="Báo sai thông tin vườn" aria-label="Báo sai thông tin vườn ${esc(p.plotCode || '')}"><span class="ms ms-sm">flag</span></button>
                  ${canManage ? `<button class="icon-btn sm" data-act="edit-plot" data-id="${esc(p.id)}" title="Sửa vườn" aria-label="Sửa vườn"><span class="ms ms-sm">edit</span></button>
                  <button class="icon-btn sm danger" data-act="del-plot" data-id="${esc(p.id)}" title="Xoá vườn" aria-label="Xoá vườn"><span class="ms ms-sm">delete</span></button>` : ''}
                </td>
              </tr>`).join('')}</tbody></table>`
            : `<p class="hint-text">Chưa có vườn nào.</p>`}
          <div id="plotsMapHost" hidden></div>
        </div>
        <div class="card">
          <h2 class="card-title"><span class="ms">history</span>Lịch sử phiếu điều tra (US C2)</h2>
          ${hh.submissions.length ? `<table class="list-table"><thead><tr><th>Loại phiếu</th><th>Vườn</th><th>Trạng thái</th><th>Chỉ số</th><th>Ngày nộp</th><th></th></tr></thead>
            <tbody>${hh.submissions.map((s) => `<tr>
                <td>${esc(formLabel(s.formKey))}</td>
                <td>${esc(s.plot?.plotCode || '—')}</td>
                <td>${statusBadge(s.status)}</td>
                <td>${renderMetricCell(s.values)}</td>
                <td>${formatDateTime(s.submittedAt)}</td>
                <td><a href="${buildHash(`/submissions/${s.id}`)}">Mở</a></td>
              </tr>`).join('')}</tbody></table>`
            : `<p class="hint-text">Chưa có phiếu nào.</p>`}
        </div>
        <div class="card" id="changeLogCard">
          <h2 class="card-title"><span class="ms">manage_history</span>Lịch sử chỉnh sửa hồ sơ (US A3)</h2>
          <p class="hint-text">Đang tải...</p>
        </div>
      </div>
      <div class="card">
        <h2 class="card-title"><span class="ms">info</span>Thông tin hộ</h2>
        <dl class="info-list">
          <div><dt>Địa bàn</dt><dd>${esc(formatRegion(hh.region))}</dd></div>
          <div><dt>Địa chỉ</dt><dd>${esc(hh.addressDetail || '—')}</dd></div>
          <div><dt>Toạ độ</dt><dd class="mono">${hh.gpsLat ?? '—'}, ${hh.gpsLng ?? '—'}</dd></div>
        </dl>
        ${canManage ? `<div class="card-danger-zone">
          <button class="btn btn-text danger" id="btnDeleteHousehold"><span class="ms">delete</span>Xoá hộ khỏi danh mục</button>
          <span class="hint-text">Hộ bị ẩn khỏi tìm kiếm; phiếu đã nộp giữ nguyên.</span>
        </div>` : ''}
      </div>
    </div>`;

  container.querySelector('#btnAddPlot').addEventListener('click', () => navigate(buildHash('/plots/new', { householdId: hh.id })));
  // Phiếu của Hộ chỉ tạo ở đây, sau khi điều tra viên đã nhìn hồ sơ (2026-11-01).
  container.querySelector('#btnNewSubmission').addEventListener('click', () => openHouseholdSubmissionDialog(hh));
  // `/submissions/new?householdId=` (dấu trang cũ, quay về từ Thêm vườn) chuyển tới đây kèm
  // `newSubmission=1`: mở luôn hộp thoại, rồi bỏ cờ khỏi URL để tải lại trang không mở lần nữa.
  if (params.newSubmission === '1') {
    history.replaceState(null, '', buildHash(`/households/${hh.id}`));
    openHouseholdSubmissionDialog(hh, { plotId: params.plotId, formKey: params.formKey });
  }

  const taiLai = () => navigate(buildHash(`/households/${hh.id}`));

  container.querySelector('#btnEditHousehold')?.addEventListener('click', () =>
    openHouseholdEditDialog(hh, taiLai),
  );

  container.querySelector('#btnDeleteHousehold')?.addEventListener('click', () => {
    // Nói rõ xoá mềm nghĩa là gì. "Bạn có chắc không?" không giúp ai quyết định.
    openDeleteDialog({
      title: `Xoá hộ "${hh.fullName}" khỏi danh mục?`,
      message: 'Hộ sẽ không còn hiện trong tìm kiếm và không tạo phiếu mới được nữa. Các phiếu đã nộp vẫn giữ nguyên.',
      remove: (reason) => api.households.remove(hh.id, reason),
      onDone: () => {
        toast('Đã xoá hộ khỏi danh mục');
        navigate(buildHash('/households'));
      },
    });
  });

  container.querySelectorAll('[data-act="edit-plot"]').forEach((btn) =>
    btn.addEventListener('click', () => {
      const plot = hh.plots.find((p) => p.id === btn.dataset.id);
      if (plot) openPlotEditDialog(plot, taiLai);
    }),
  );

  container.querySelectorAll('[data-act="del-plot"]').forEach((btn) =>
    btn.addEventListener('click', () => {
      const plot = hh.plots.find((p) => p.id === btn.dataset.id);
      if (!plot) return;
      openDeleteDialog({
        title: `Xoá vườn "${plot.plotCode || plot.id}" khỏi danh mục?`,
        message: 'Vườn sẽ không còn hiện khi tạo phiếu mới. Phiếu đã nộp giữ nguyên diện tích đã chốt tại thời điểm nộp.',
        remove: (reason) => api.plots.remove(plot.id, reason),
        onDone: () => {
          toast('Đã xoá vườn');
          taiLai();
        },
      });
    }),
  );

  renderChangeLog(container.querySelector('#changeLogCard'), () => api.households.changes(hh.id));

  // Doc/11a E2 — đề xuất sửa từ thực địa. Nạp sau khi hồ sơ đã hiện: thiếu nhãn "chờ duyệt"
  // vài trăm mili-giây còn hơn bắt điều tra viên nhìn màn trắng.
  const plotById = new Map(hh.plots.map((p) => [p.id, p]));
  const titleOf = (cr) =>
    cr.entity === 'plot' ? `Vườn ${plotById.get(cr.entityId)?.plotCode || ''}`.trim() : `Hộ ${hh.fullName}`;
  let proposals = [];
  const showProposals = async () => {
    proposals = await loadOpenProposals([hh.id, ...hh.plots.map((p) => p.id)]);
    if (!container.isConnected) return;
    renderOpenProposals(container.querySelector('#proposalsCard'), proposals, { titleOf, onChanged: showProposals });
    container.querySelectorAll('.pending-slot').forEach((slot) => {
      const ofRecord = proposals.filter((cr) => cr.entityId === slot.dataset.entityId);
      const pend = pendingByField(ofRecord).get(slot.dataset.field);
      slot.innerHTML = pend
        ? `<span class="pending-chip">Đề xuất: <b>${esc(displayValue('plot', slot.dataset.field, pend.value) ?? '—')}</b> · ${
            pend.status === 'queued' ? 'chờ mạng' : 'chờ duyệt'
          }</span>`
        : '';
    });
  };
  void showProposals();

  const link = buildHash(`/households/${hh.id}`);
  container.querySelector('#btnReportHousehold').addEventListener('click', () =>
    openProposalSheet({ entity: 'household', record: hh, title: hh.fullName, link, openProposals: proposals, onSent: showProposals }),
  );
  container.querySelectorAll('[data-act="report-plot"]').forEach((btn) =>
    btn.addEventListener('click', () => {
      const plot = plotById.get(btn.dataset.id);
      if (!plot) return;
      openProposalSheet({
        entity: 'plot',
        record: plot,
        title: plot.plotCode || 'Vườn',
        link,
        openProposals: proposals,
        onSent: showProposals,
      });
    }),
  );

  // Xem mảnh đã vẽ ngay trong hồ sơ, không phải mở màn vẽ. Nạp sau cùng: bản đồ không được làm chậm
  // phần còn lại của hồ sơ.
  const plotsMap = mountPlotsMap(container.querySelector('#plotsMapHost'), hh.plots);
  container.querySelectorAll('[data-act="focus-plot"]').forEach((btn) =>
    btn.addEventListener('click', async () => (await plotsMap)?.focus(btn.dataset.id)),
  );
  return () => {
    void plotsMap.then((map) => map?.destroy());
  };
}


/** US A3 — sửa hồ sơ Hộ ngoài luồng phiếu điều tra. */
function openHouseholdEditDialog(hh, onSaved) {
  let regionPicker = null;
  openFormDialog({
    title: `Sửa hồ sơ: ${hh.fullName}`,
    bodyHtml: `
      <div class="form-grid">
        <div class="form-row"><label>Họ tên chủ hộ *</label><input name="fullName" value="${esc(hh.fullName)}" required></div>
        <div class="form-row"><label>Số điện thoại</label><input name="phone" value="${esc(hh.phone || '')}"></div>
        <div class="form-row"><label>CCCD</label><input name="idCardNumber" value="${esc(hh.idCardNumber || '')}"></div>
        <div class="form-row"><label>Mã nông dân (IDH)</label><input name="farmerCode" value="${esc(hh.farmerCode || '')}"></div>
        <div id="editRegionHost" class="contents"></div>
        <div class="form-row"><label>Địa chỉ chi tiết</label><input name="addressDetail" value="${esc(hh.addressDetail || '')}"></div>
        <div class="form-row"><label>Toạ độ GPS (lat, lng)</label>
          <div class="input-pair">
            <input name="gpsLat" placeholder="Vĩ độ" inputmode="decimal" value="${esc(hh.gpsLat ?? '')}">
            <input name="gpsLng" placeholder="Kinh độ" inputmode="decimal" value="${esc(hh.gpsLng ?? '')}">
          </div>
        </div>
        ${EDIT_REASON_FIELD_HTML}
      </div>`,
    onMount: (form) => {
      regionPicker = createRegionPicker(form.querySelector('#editRegionHost'), { value: hh.region?.id });
    },
    onSubmit: async (form) => {
      const payload = {
        fullName: form.fullName.value.trim(),
        // Chuỗi rỗng nghĩa là "xoá giá trị này", nên gửi null chứ không phải bỏ
        // trường đi — bỏ đi thì máy chủ hiểu là "không đổi" và giá trị cũ ở lại.
        phone: form.phone.value.trim() || null,
        idCardNumber: form.idCardNumber.value.trim() || null,
        farmerCode: form.farmerCode.value.trim() || null,
        regionId: regionPicker.getValue(),
        addressDetail: form.addressDetail.value.trim() || null,
        gpsLat: form.gpsLat.value ? Number(form.gpsLat.value) : null,
        gpsLng: form.gpsLng.value ? Number(form.gpsLng.value) : null,
      };
      if (!payload.fullName) return 'Họ tên chủ hộ không được để trống.';
      const reasonError = editReasonError(form);
      if (reasonError) return reasonError;
      payload.reason = form.reason.value.trim();

      try {
        await api.households.update(hh.id, payload);
      } catch (err) {
        if (err.code === 'CONFLICT' && err.existing?.id) {
          return { html: `${esc(err.message)} — <a href="${buildHash(`/households/${err.existing.id}`)}">mở hồ sơ hộ đó</a>` };
        }
        return err.message;
      }
      toast('Đã cập nhật hồ sơ hộ');
      onSaved();
    },
  });
}

/**
 * Mã vườn kiểu `Eatan.TranVanMinh.1` không có khoảng trắng nên không xuống dòng được, và cột Mã
 * vườn đẩy bảng tràn khỏi thẻ ở màn 1100px. `<wbr>` sau . _ - cho phép ngắt ở đó khi chật — màn
 * rộng vẫn một dòng.
 */
function breakableCode(code) {
  return code ? esc(code).replace(/([._-])/g, '$1<wbr>') : '—';
}

/** Sửa Vườn. Không cho đổi Hộ chủ quản — `UpdatePlotDto` cũng không nhận `householdId`. */
function openPlotEditDialog(plot, onSaved) {
  openFormDialog({
    title: `Sửa vườn: ${plot.plotCode || plot.id}`,
    bodyHtml: `
      <div class="form-grid">
        <div class="form-row"><label>Mã vườn</label><input name="plotCode" value="${esc(plot.plotCode || '')}"></div>
        <div class="form-row"><label>Diện tích (ha) *</label><input name="areaHa" inputmode="decimal" value="${esc(plot.areaHa)}" required></div>
        ${soilTypeSelectHtml({ value: plot.soilType, allowUnclassified: true })}
        <div class="form-row"><label>Toạ độ GPS (lat, lng)</label>
          <div class="input-pair">
            <input name="gpsLat" placeholder="Vĩ độ" inputmode="decimal" value="${esc(plot.gpsLat ?? '')}">
            <input name="gpsLng" placeholder="Kinh độ" inputmode="decimal" value="${esc(plot.gpsLng ?? '')}">
          </div>
        </div>
        <div class="form-row"><label>Ranh giới trên bản đồ</label>
          <div class="input-pair boundary-row">
            <span>${esc(describeBoundary(plot.boundaryGeojson) || 'Chưa vẽ')}</span>
            <a class="btn btn-tonal sm" data-act="boundary" href="${buildHash(`/plots/${plot.id}/boundary`)}"><span class="ms ms-sm">map</span>${plot.boundaryGeojson ? 'Sửa ranh giới' : 'Vẽ ranh giới'}</a>
          </div>
        </div>
        ${EDIT_REASON_FIELD_HTML}
      </div>
      <p class="hint-text">Sửa diện tích <b>không</b> làm thay đổi phiếu đã nộp — mỗi phiếu
         giữ diện tích đã chốt tại thời điểm nộp. Mở bản đồ là đóng hộp thoại này — bấm Lưu trước
         nếu vừa sửa thông tin khác.</p>`,
    // Hộp thoại này vẫn nhập toạ độ bằng tay: yêu cầu 2026-08-23 chỉ đặt nút lấy toạ độ ở
    // hai luồng tạo mới (Tạo hộ, Thêm vườn). Muốn có ở đây thì đổi cặp ô GPS phía trên
    // thành `${gpsFieldHtml({ latValue: plot.gpsLat, lngValue: plot.gpsLng })}` rồi gọi
    // thêm `attachGpsCapture(form)` — helper đã lo phần hỏi trước khi ghi đè giá trị cũ.
    onMount: (form) => {
      attachSoilTypeHint(form);
      // Ranh giới lưu qua màn bản đồ riêng (`PUT /plots/:id/boundary`), không qua nút Lưu ở đây.
      form.querySelector('[data-act="boundary"]').addEventListener('click', (e) => {
        e.preventDefault();
        form.querySelector('[data-act="close"]').click();
        navigate(buildHash(`/plots/${plot.id}/boundary`));
      });
    },
    onSubmit: async (form) => {
      const areaHa = Number(form.areaHa.value);
      if (!Number.isFinite(areaHa) || areaHa <= 0) return 'Diện tích phải là số lớn hơn 0.';
      // Vườn tạo trước 2026-08-23 có thể chưa phân loại đất (hoặc giữ một giá trị text tự
      // do của bản cũ). Hộp thoại giữ nguyên trạng đó ở ô chọn và bắt chọn lại ở đây —
      // tự gán về mặc định là ghi vào DB một phân loại chưa ai nhìn thấy để xác nhận.
      if (!isSoilType(form.soilType.value)) return SOIL_TYPE_REQUIRED_MESSAGE;
      const reasonError = editReasonError(form);
      if (reasonError) return reasonError;

      const payload = {
        reason: form.reason.value.trim(),
        plotCode: form.plotCode.value.trim() || null,
        areaHa,
        soilType: form.soilType.value,
        gpsLat: form.gpsLat.value ? Number(form.gpsLat.value) : null,
        gpsLng: form.gpsLng.value ? Number(form.gpsLng.value) : null,
      };

      try {
        await api.plots.update(plot.id, payload);
      } catch (err) {
        return err.message;
      }
      toast('Đã cập nhật vườn');
      onSaved();
    },
  });
}

/**
 * Chỉ số của một phiếu, gọn trong một ô bảng. US C2 muốn thấy *biến động* giữa các
 * đợt điều tra — muốn so sánh thì các con số phải nằm cạnh nhau trên cùng một
 * bảng, không phải nằm rải trong từng phiếu phải mở ra mới thấy.
 */
function renderMetricCell(values) {
  const đãSắp = sortMetrics(values || []).filter((v) => v.metricKey !== 'area_ha');
  if (!đãSắp.length) return '<span class="hint-text">—</span>';
  return `<div class="metric-cell">${đãSắp
    .map((v) => `<span class="metric-chip" title="${esc(metricLabel(v.metricKey))}">
        <span class="metric-chip-label">${esc(metricLabel(v.metricKey))}</span>
        <b>${esc(formatMetricValue(v))}</b></span>`)
    .join('')}</div>`;
}
